/**
 * Exchange Market System
 * Independent per-coin price simulation: momentum + mean reversion + noise
 * (same family as marketSentimentSystem, but per coin and OHLC-candle based).
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { COINS, EXCHANGE_CONFIG, TIMEFRAMES, type CoinConfig, type Timeframe } from '../config/coins.js';
import { MANIPULATIONS, MANIP_COIN_IMPACT_SCALE } from '../config/market.js';
import type { Candle, CoinMarket } from '../exchangeState.js';
import { whaleBias } from './whaleSystem.js';
import { scenarioBias, scenarioVolMul } from './scenarioSystem.js';
import { rugBias, rugPriceOverride } from './rugPullSystem.js';
import { onboardingPumpPrice } from '../onboardingState.js';

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

/** Global sentiment bias: old market multiplier tilts every coin's momentum */
function sentimentBias(state: GameState, cfg: CoinConfig): number {
  const mult = state.market?.earningsMultiplier ?? 1;
  const k = cfg.tier === 'major' ? EXCHANGE_CONFIG.sentimentKMajor : EXCHANGE_CONFIG.sentimentKMeme;
  return (mult - EXCHANGE_CONFIG.sentimentAnchor) * k * cfg.volatility;
}

/** Coin-targeted manipulation push (backfire = negative impact, decided at dispatch) */
function manipulationBias(state: GameState, cfg: CoinConfig, now: number): number {
  const manip = state.market?.activeManipulation;
  if (!manip || manip.coinId !== cfg.id || now >= manip.expiresAt) return 0;
  const duration = MANIPULATIONS.find(m => m.id === manip.id)?.duration ?? 15_000;
  const remaining = clamp((manip.expiresAt - now) / duration, 0, 1);
  return manip.impact * MANIP_COIN_IMPACT_SCALE * cfg.volatility * remaining;
}

/** Active coin-targeted news push (decays with remaining effect time) */
function newsBias(state: GameState, cfg: CoinConfig, now: number): number {
  let bias = 0;
  for (const news of state.info?.activeNews ?? []) {
    if (news.coinId !== cfg.id || now >= news.expiresAt) continue;
    // embargoed (front-run window): no price effect until startsAt
    if (news.startsAt !== undefined && now < news.startsAt) continue;
    const span = news.expiresAt - news.publishedAt;
    const remaining = span > 0 ? clamp((news.expiresAt - now) / span, 0, 1) : 0;
    bias += news.direction * news.impact * cfg.volatility * remaining;
  }
  return bias;
}

/**
 * Sum of all external momentum pushes for one coin (v2.0 system integration).
 * Sources: global sentiment + manipulation + news + whale + scenario + rug
 * recovery. The sum is clamped so stacked events cannot explode the price.
 */
export function externalBias(state: GameState, cfg: CoinConfig, price: number, now: number): number {
  let bias = sentimentBias(state, cfg) + manipulationBias(state, cfg, now) + newsBias(state, cfg, now);
  const whale = state.whales?.[cfg.id];
  if (whale) bias += whaleBias(whale, price, cfg);
  bias += scenarioBias(state.risk?.scenarios[cfg.id], cfg, price, now);
  bias += rugBias(state.risk?.rugs[cfg.id], cfg);
  const cap = cfg.volatility * EXCHANGE_CONFIG.momentumClampFactor;
  return clamp(bias, -cap, cap);
}

/** One price step: momentum decays, noise pushes, price reverts toward anchor */
function stepPrice(
  coin: CoinMarket,
  cfg: CoinConfig,
  extBias: number,
  volMul: number
): { price: number; momentum: number } {
  const noise = (Math.random() - 0.5) * 2 * cfg.volatility * volMul;
  const reversion = Math.log(cfg.startPrice / coin.price) * cfg.meanReversionStrength;
  const maxMomentum = cfg.volatility * EXCHANGE_CONFIG.momentumClampFactor;
  const momentum = clamp(
    coin.momentum * cfg.momentumDecay + noise + reversion + extBias,
    -maxMomentum,
    maxMomentum
  );
  const price = Math.max(cfg.minPrice, coin.price * (1 + momentum));
  return { price, momentum };
}

/** Update forming candle or roll over to a new bucket. Returns a new array. */
function updateCandles(candles: Candle[], tf: Timeframe, price: number, now: number): Candle[] {
  const tfMs = TIMEFRAMES[tf];
  const bucket = Math.floor(now / tfMs) * tfMs;
  const last = candles[candles.length - 1];

  if (last && last.t === bucket) {
    const updated: Candle = {
      ...last,
      h: Math.max(last.h, price),
      l: Math.min(last.l, price),
      c: price,
    };
    return [...candles.slice(0, -1), updated];
  }

  const open = last ? last.c : price;
  const next: Candle = { t: bucket, o: open, h: Math.max(open, price), l: Math.min(open, price), c: price };
  const result = [...candles, next];
  if (result.length > EXCHANGE_CONFIG.maxCandles) {
    return result.slice(-EXCHANGE_CONFIG.maxCandles);
  }
  return result;
}

function tickCoin(state: GameState, coin: CoinMarket, cfg: CoinConfig, now: number): CoinMarket {
  // Rug crash/floor phases override the normal momentum walk entirely.
  // The scripted onboarding pump also overrides price (target coin only, step 3).
  const rug = rugPriceOverride(state.risk?.rugs[cfg.id], cfg, coin.price, now);
  const override = rug !== null ? rug : onboardingPumpPrice(state.onboarding, cfg.id, coin.price, now);
  const { price, momentum } = override !== null
    ? { price: override, momentum: 0 }
    : stepPrice(
        coin, cfg,
        externalBias(state, cfg, coin.price, now),
        scenarioVolMul(state.risk?.scenarios[cfg.id], now)
      );
  const candles = {} as Record<Timeframe, Candle[]>;
  for (const tf of Object.keys(TIMEFRAMES) as Timeframe[]) {
    candles[tf] = updateCandles(coin.candles[tf], tf, price, now);
  }
  return { price, momentum, candles };
}

export const exchangeMarketSystem: TickSystem<GameState, GameAction> = {
  name: 'exchangeMarket',

  update(state) {
    if (!state.isRunning) return state;
    if (!state.exchange) return state;

    const now = Date.now();
    if (now - state.exchange.lastTickTime < EXCHANGE_CONFIG.tickInterval) return state;

    // Only listed coins tick; delisted / pool coins stay frozen at their last price.
    const coins: Record<string, CoinMarket> = { ...state.exchange.coins };
    for (const cfg of COINS) {
      if (state.exchange.listing[cfg.id]?.status !== 'listed') continue;
      const coin = state.exchange.coins[cfg.id];
      if (coin) coins[cfg.id] = tickCoin(state, coin, cfg, now);
    }

    return {
      ...state,
      exchange: {
        ...state.exchange,
        coins,
        lastTickTime: now,
      },
    };
  },
};
